/*
 * Rewind Currency Converter
 * ------------------------------------------------------------------
 * Display-only currency conversion for the Rewind Shopify theme.
 * Replaces the "Currency Converter+" app.
 *
 * How it works
 *   1. The store sells in one currency (AED). Every price Liquid prints
 *      goes through the shop money format, which wraps it in
 *      <span class="money">...</span>.
 *   2. We work out the visitor's currency (manual choice, then the
 *      country Shopify detects from their IP, then their timezone).
 *   3. Each .money element's AED amount is remembered and its text is
 *      replaced with the converted amount. A MutationObserver catches
 *      prices the theme renders later (cart drawer, variant changes,
 *      quick add, predictive search, apps).
 *   4. Checkout is untouched: orders are still charged in AED, and a
 *      note in the cart says so.
 *
 * Config comes from window.RewindCurrencyConfig (snippets/rewind-currency.liquid).
 */
(function () {
  'use strict';

  var STORAGE_CHOICE = 'rewind-currency:choice';
  var STORAGE_COUNTRY = 'rewind-currency:country';
  var COUNTRY_TTL_MS = 24 * 60 * 60 * 1000;

  var cfg = Object.assign(
    {
      baseCurrency: 'AED',
      activeCurrency: null,
      currencies: ['AED', 'SAR', 'KWD', 'QAR', 'BHD', 'OMR', 'USD', 'EUR', 'GBP'],
      defaultCurrency: null,
      format: '[code] [amount]',
      rounding: 'whole',
      moneySelector: '.money',
      extraSelectors: [],
      switcherMounts: [],
      floatingSwitcher: true,
      cartNoteSelectors: ['.totals'],
      cartNote: 'Prices in [currency] are approximate. Your order will be charged in [base].',
      geoEndpoint: '/browsing_context_suggestions.json',
      geoTimeoutMs: 1500,
    },
    window.RewindCurrencyConfig || {}
  );

  var html = document.documentElement;
  function reveal() {
    html.classList.remove('rc-pending');
  }

  // If the storefront is already showing a non-base currency (e.g. Shopify
  // Markets local currencies get turned on later), converting again would
  // double-convert. Do nothing in that case.
  var shopifyActive = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || cfg.activeCurrency;
  if (shopifyActive && shopifyActive !== cfg.baseCurrency) {
    reveal();
    return;
  }

  /* ------------------------------------------------------------------ */
  /* Exchange rates                                                      */
  /* ------------------------------------------------------------------ */

  // Official pegs, in USD per unit. Used to sanity-check Shopify's feed and
  // as a fallback when the feed can't be loaded.
  var PEGGED = {
    USD: 1,
    AED: 1 / 3.6725,
    SAR: 1 / 3.75,
    QAR: 1 / 3.64,
    BHD: 1 / 0.376,
    OMR: 1 / 0.3845,
    JOD: 1 / 0.709,
  };

  // Returns a map of "USD value of one unit" per currency code.
  function loadRates() {
    var feed = window.Currency && window.Currency.rates;
    if (feed && feed.AED && feed.USD) {
      var ratio = feed.AED / feed.USD;
      // Shopify's feed is USD per unit, so AED/USD should be ~0.2723.
      if (Math.abs(ratio - PEGGED.AED) / PEGGED.AED < 0.02) return feed;
      // Inverted feed (units per USD): flip it.
      if (Math.abs(1 / ratio - PEGGED.AED) / PEGGED.AED < 0.02) {
        var flipped = {};
        for (var k in feed) if (feed[k]) flipped[k] = 1 / feed[k];
        return flipped;
      }
    }
    return PEGGED;
  }

  var rates = loadRates();

  function supported(code) {
    return !!(code && rates[code] && rates[cfg.baseCurrency]);
  }

  function convert(amount, to) {
    return (amount * rates[cfg.baseCurrency]) / rates[to];
  }

  /* ------------------------------------------------------------------ */
  /* Country -> currency                                                 */
  /* ------------------------------------------------------------------ */

  var EURO = 'AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK AD MC SM VA ME XK';
  var COUNTRY_CURRENCY = {
    AE: 'AED', SA: 'SAR', KW: 'KWD', QA: 'QAR', BH: 'BHD', OM: 'OMR',
    JO: 'JOD', EG: 'EGP', LB: 'LBP', IQ: 'IQD', MA: 'MAD', TN: 'TND', DZ: 'DZD', TR: 'TRY',
    US: 'USD', CA: 'CAD', MX: 'MXN', BR: 'BRL', GB: 'GBP', CH: 'CHF', NO: 'NOK', SE: 'SEK',
    DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON', UA: 'UAH', RU: 'RUB',
    IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR', CN: 'CNY', HK: 'HKD', TW: 'TWD',
    JP: 'JPY', KR: 'KRW', SG: 'SGD', MY: 'MYR', ID: 'IDR', TH: 'THB', PH: 'PHP', VN: 'VND',
    AU: 'AUD', NZ: 'NZD', ZA: 'ZAR', NG: 'NGN', KE: 'KES',
  };
  EURO.split(' ').forEach(function (c) {
    COUNTRY_CURRENCY[c] = 'EUR';
  });

  var TIMEZONE_COUNTRY = {
    'Asia/Riyadh': 'SA', 'Asia/Dubai': 'AE', 'Asia/Kuwait': 'KW', 'Asia/Qatar': 'QA',
    'Asia/Bahrain': 'BH', 'Asia/Muscat': 'OM', 'Asia/Amman': 'JO', 'Africa/Cairo': 'EG',
    'Asia/Beirut': 'LB', 'Asia/Baghdad': 'IQ', 'Europe/Istanbul': 'TR', 'Europe/London': 'GB',
    'Europe/Paris': 'FR', 'Europe/Berlin': 'DE', 'Europe/Madrid': 'ES', 'Europe/Rome': 'IT',
    'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Vienna': 'AT', 'Europe/Dublin': 'IE',
    'Europe/Lisbon': 'PT', 'Europe/Athens': 'GR', 'Europe/Zurich': 'CH', 'Asia/Kolkata': 'IN',
    'Asia/Calcutta': 'IN', 'Asia/Karachi': 'PK', 'Asia/Singapore': 'SG', 'Asia/Tokyo': 'JP',
    'Australia/Sydney': 'AU', 'America/New_York': 'US', 'America/Chicago': 'US',
    'America/Denver': 'US', 'America/Los_Angeles': 'US', 'America/Phoenix': 'US',
    'America/Toronto': 'CA', 'America/Vancouver': 'CA',
  };

  function currencyForCountry(country) {
    var code = COUNTRY_CURRENCY[(country || '').toUpperCase()];
    return supported(code) ? code : null;
  }

  function timezoneCountry() {
    try {
      return TIMEZONE_COUNTRY[Intl.DateTimeFormat().resolvedOptions().timeZone] || null;
    } catch (e) {
      return null;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Storage (can throw in private mode / blocked storage)               */
  /* ------------------------------------------------------------------ */

  function read(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function write(key, value) {
    try {
      if (value == null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (e) {}
  }

  function cachedCountry() {
    try {
      var c = JSON.parse(read(STORAGE_COUNTRY));
      if (c && c.country && Date.now() - c.at < COUNTRY_TTL_MS) return c.country;
    } catch (e) {}
    return null;
  }

  // Shopify's storefront endpoint that reports the country it detected from
  // the visitor's IP.
  function detectCountry() {
    var cached = cachedCountry();
    if (cached) return Promise.resolve(cached);
    if (!window.fetch) return Promise.resolve(null);

    var timeout = new Promise(function (resolve) {
      setTimeout(resolve, cfg.geoTimeoutMs, null);
    });
    var lookup = fetch(cfg.geoEndpoint, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        var c = data && data.detected_values && data.detected_values.country;
        var handle = c && (c.handle || c.code);
        if (handle) write(STORAGE_COUNTRY, JSON.stringify({ country: handle.toUpperCase(), at: Date.now() }));
        return handle ? handle.toUpperCase() : null;
      })
      .catch(function () {
        return null;
      });
    return Promise.race([lookup, timeout]);
  }

  /* ------------------------------------------------------------------ */
  /* Formatting                                                          */
  /* ------------------------------------------------------------------ */

  function fractionDigits(code) {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions()
        .maximumFractionDigits;
    } catch (e) {
      return 2;
    }
  }

  function symbolFor(code) {
    try {
      var parts = new Intl.NumberFormat('en', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
        .formatToParts(0);
      for (var i = 0; i < parts.length; i++) if (parts[i].type === 'currency') return parts[i].value;
    } catch (e) {}
    return code;
  }

  function formatNumber(amount, code) {
    var digits = fractionDigits(code);
    if (cfg.rounding === 'whole') {
      amount = Math.round(amount);
      digits = 0;
    } else if (cfg.rounding === 'up') {
      amount = Math.ceil(amount);
      digits = 0;
    }
    return new Intl.NumberFormat('en', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  }

  function format(amount, code) {
    var number = formatNumber(amount, code);
    return cfg.format.replace('[code]', code).replace('[symbol]', symbolFor(code)).replace('[amount]', number);
  }

  // Shop-currency prices keep the store's own format; only the number is
  // rounded (when rounding is on).
  function formatBase(s) {
    if (cfg.rounding === 'none') return s.original;
    return s.original.replace(AMOUNT_RE, formatNumber(s.base, cfg.baseCurrency));
  }

  // Pulls the amount out of text such as "د.إ.1,234.50AED" or "Dhs. 150".
  // The Arabic symbol contains dots, so match the first digit run only.
  var AMOUNT_RE = /\d[\d,]*(?:\.\d+)?/;
  function parseAmount(text) {
    var m = AMOUNT_RE.exec(text || '');
    if (!m) return null;
    var n = parseFloat(m[0].replace(/,/g, ''));
    return isFinite(n) ? n : null;
  }

  /* ------------------------------------------------------------------ */
  /* Converting the page                                                 */
  /* ------------------------------------------------------------------ */

  var current = cfg.baseCurrency;
  var state = new WeakMap(); // element -> { base, original, rendered }

  function selector() {
    return [cfg.moneySelector].concat(cfg.extraSelectors).join(',');
  }

  function isTarget(el) {
    if (!el.matches(selector())) return false;
    // A container (e.g. a cart total <p>) that already holds a .money span is
    // handled through the span.
    return !(el.querySelector && el.querySelector(cfg.moneySelector));
  }

  function render(el) {
    var text = el.textContent;
    var s = state.get(el);
    // Text changed since we last wrote it: the theme or an app put a new AED
    // price here.
    if (!s || text !== s.rendered) {
      var amount = parseAmount(text);
      if (amount == null) return;
      s = { base: amount, original: text };
      state.set(el, s);
    }
    var next = current === cfg.baseCurrency ? formatBase(s) : format(convert(s.base, current), current);
    s.rendered = next;
    if (text !== next) el.textContent = next;
    el.setAttribute('data-rc-currency', current);
  }

  function renderWithin(root) {
    if (!root || root.nodeType !== 1) return;
    if (isTarget(root)) render(root);
    var els = root.querySelectorAll(selector());
    for (var i = 0; i < els.length; i++) if (isTarget(els[i])) render(els[i]);
  }

  function renderNotes() {
    var show = current !== cfg.baseCurrency && cfg.cartNote;
    var text = show ? cfg.cartNote.replace('[currency]', current).replace('[base]', cfg.baseCurrency) : '';
    cfg.cartNoteSelectors.forEach(function (sel) {
      var hosts = document.querySelectorAll(sel);
      for (var i = 0; i < hosts.length; i++) {
        var host = hosts[i];
        var note = host.nextElementSibling;
        var hasNote = note && note.classList.contains('rc-note');
        if (!show) {
          if (hasNote) note.remove();
          continue;
        }
        if (!hasNote) {
          note = document.createElement('p');
          note.className = 'rc-note';
          host.insertAdjacentElement('afterend', note);
        }
        if (note.textContent !== text) note.textContent = text;
      }
    });
  }

  function renderAll() {
    renderWithin(document.body);
    renderNotes();
    syncSwitchers();
  }

  var observer = new MutationObserver(function (mutations) {
    var notesDirty = false;
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type === 'characterData') {
        var parent = m.target.parentElement;
        var host = parent && parent.closest(selector());
        if (host && isTarget(host)) render(host);
        continue;
      }
      var target = m.target.nodeType === 1 ? m.target.closest(selector()) : null;
      if (target && isTarget(target)) render(target);
      for (var j = 0; j < m.addedNodes.length; j++) {
        var node = m.addedNodes[j];
        if (node.nodeType !== 1) continue;
        if (node.classList.contains('rc-note') || node.classList.contains('rc-switcher')) continue;
        renderWithin(node);
        notesDirty = true;
      }
    }
    if (notesDirty) {
      renderNotes();
      mountSwitchers();
    }
  });

  /* ------------------------------------------------------------------ */
  /* Switcher                                                            */
  /* ------------------------------------------------------------------ */

  function switcherCurrencies() {
    var list = cfg.currencies.filter(supported);
    if (list.indexOf(cfg.baseCurrency) === -1) list.unshift(cfg.baseCurrency);
    if (list.indexOf(current) === -1) list.push(current);
    return list;
  }

  function buildSwitcher(floating) {
    var wrap = document.createElement('div');
    wrap.className = 'rc-switcher' + (floating ? ' rc-switcher--floating' : '');
    var select = document.createElement('select');
    select.className = 'rc-switcher__select';
    select.setAttribute('aria-label', 'Currency');
    switcherCurrencies().forEach(function (code) {
      var opt = document.createElement('option');
      opt.value = code;
      opt.textContent = code;
      select.appendChild(opt);
    });
    select.value = current;
    select.addEventListener('change', function () {
      api.set(select.value);
    });
    wrap.appendChild(select);
    return wrap;
  }

  function mountSwitchers() {
    var mounted = false;
    cfg.switcherMounts.forEach(function (sel) {
      var hosts = document.querySelectorAll(sel);
      for (var i = 0; i < hosts.length; i++) {
        mounted = true;
        if (!hosts[i].querySelector(':scope > .rc-switcher')) hosts[i].prepend(buildSwitcher(false));
      }
    });
    if (!mounted && cfg.floatingSwitcher && !document.querySelector('.rc-switcher--floating')) {
      document.body.appendChild(buildSwitcher(true));
    }
  }

  function syncSwitchers() {
    var selects = document.querySelectorAll('.rc-switcher__select');
    for (var i = 0; i < selects.length; i++) {
      var sel = selects[i];
      if (!sel.querySelector('option[value="' + current + '"]')) {
        var opt = document.createElement('option');
        opt.value = opt.textContent = current;
        sel.appendChild(opt);
      }
      sel.value = current;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Public API                                                          */
  /* ------------------------------------------------------------------ */

  var api = {
    get: function () {
      return current;
    },
    set: function (code, opts) {
      code = (code || '').toUpperCase();
      if (!supported(code)) return false;
      current = code;
      if (!opts || opts.remember !== false) write(STORAGE_CHOICE, code);
      renderAll();
      html.setAttribute('data-rc-currency', code);
      document.dispatchEvent(new CustomEvent('rewind:currency:change', { detail: { currency: code } }));
      return true;
    },
    reset: function () {
      write(STORAGE_CHOICE, null);
      write(STORAGE_COUNTRY, null);
    },
    convert: function (amount, to) {
      return convert(amount, (to || current).toUpperCase());
    },
    format: function (amount, code) {
      return format(amount, (code || current).toUpperCase());
    },
    rates: rates,
    usingFallbackRates: rates === PEGGED,
  };
  window.RewindCurrency = api;

  /* ------------------------------------------------------------------ */
  /* Start                                                               */
  /* ------------------------------------------------------------------ */

  function pickCurrency() {
    // ?currency=SAR in the URL: handy for testing and sharing links.
    var fromUrl = new URLSearchParams(window.location.search).get('currency');
    if (fromUrl && supported(fromUrl.toUpperCase())) {
      write(STORAGE_CHOICE, fromUrl.toUpperCase());
      return Promise.resolve(fromUrl.toUpperCase());
    }
    var chosen = read(STORAGE_CHOICE);
    if (supported(chosen)) return Promise.resolve(chosen);

    return detectCountry().then(function (country) {
      return (
        currencyForCountry(country) ||
        currencyForCountry(timezoneCountry()) ||
        (supported(cfg.defaultCurrency) ? cfg.defaultCurrency : cfg.baseCurrency)
      );
    });
  }

  function start() {
    mountSwitchers();
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    pickCurrency()
      .then(function (code) {
        api.set(code, { remember: false });
      })
      .catch(function () {})
      .then(reveal);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
